import { DESCRIPTION_PROSE_CLASS } from "components/description-prose";
import { StatBand } from "../event-experience/StatBand";
import { ownStatTiles } from "../event-experience/landing-model";
import type { PublicEvent } from "./schemas";

/** The bare page's centred column, with the notch kept clear in landscape. */
const PAGE_COLUMN =
  "mx-auto w-full max-w-3xl pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))]";

/**
 * What follows the bold hero for a signed-in reader who is not in the event:
 * the description and the organiser's own stat tiles.
 *
 * The description is the sanitised HTML the projection carries, set in the
 * page's own type like the Overview's. The public projection has no agenda, so
 * there are no chapters and no computed numbers: only the tiles the organiser
 * entered are drawn, and none when there are none.
 *
 * A signed-out visitor does not get this block at all, only the hero, the join
 * box and the footer line below.
 */
export function PublicEventBody({ event }: { event: PublicEvent }) {
  const tiles = ownStatTiles(event);

  return (
    <div
      className="flex min-w-0 flex-col gap-8 py-9"
      data-testid="public-event-body"
    >
      {event.description && (
        <div
          className={DESCRIPTION_PROSE_CLASS}
          data-testid="event-description"
          dangerouslySetInnerHTML={{ __html: event.description }}
        />
      )}
      <StatBand tiles={tiles} />
    </div>
  );
}

/** The signed-out page's one footer line, in the page's centred column. */
export function PublicEventFooter({ event }: { event: PublicEvent }) {
  return (
    <p
      className={`${PAGE_COLUMN} pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-8 text-sm text-muted-foreground wrap-anywhere`}
      data-testid="public-event-footer"
    >
      Holtzbrinck Connect · {event.title}
    </p>
  );
}
